<script setup lang="ts">
import type { AppModuleCustomPricingSchemaBulkInsertResponse } from "@classes-service-types/index.ts";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import FileUpload from "primevue/fileupload";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { ref } from "vue";
import { useInsertCustomPricing } from "@/providers/customPricing/useBulkInsertCustomPricing";

defineOptions({ name: "BulkInsertModal" });

const props = withDefaults(
  defineProps<{
    visible: boolean;
  }>(),
  {
    visible: false,
  },
);

const emit = defineEmits<(e: "update:visible", value: boolean) => void>();

const toast = useToast();
const result = ref<AppModuleCustomPricingSchemaBulkInsertResponse | undefined>(undefined);
const selectedFile = ref<File | undefined>(undefined);

const { mutate: bulkInsert, isPending: isUploading } = useInsertCustomPricing();

const handleFileSelect = (event: { files: File[] }): void => {
  selectedFile.value = event.files?.[0];
};

const handleUpload = (): void => {
  if (!selectedFile.value) {
    return;
  }

  bulkInsert(selectedFile.value, {
    onSuccess: (data) => {
      result.value = data;
      toast.add({
        severity: data.error_count && data.error_count > 0 ? "warn" : "success",
        summary: "Bulk Insert",
        detail: `Inserted: ${data.inserted_count ?? 0} | Errors: ${data.error_count ?? 0}`,
        group: "br-bi",
        life: 5000,
      });
      selectedFile.value = undefined;
    },
    onError: (error) => {
      toast.add({
        severity: "error",
        summary: "Bulk Insert",
        detail: `Upload failed. ${error.message}`,
        group: "br-bi",
        life: 5000,
      });
    },
  });
};

const handleHide = (): void => {
  result.value = undefined;
  selectedFile.value = undefined;
  emit("update:visible", false);
};
</script>

<template>
  <Toast position="bottom-right" group="br-bi" />
  <Dialog
    :visible="visible"
    @update:visible="handleHide"
    header="Bulk Import Custom Pricing"
    :modal="true"
    :closable="true"
    :style="{ width: '600px' }"
  >
    <div class="flex flex-col gap-6 py-2">
      <p class="text-sm text-gray-600">
        Upload a CSV file to bulk-import custom pricing overrides. Each row should contain
        <code class="bg-gray-100 px-1 rounded text-xs">session_id</code>,
        <code class="bg-gray-100 px-1 rounded text-xs">home_institution_id</code>, and optional
        price fields.
      </p>

      <FileUpload
        mode="basic"
        accept=".csv"
        :auto="false"
        choose-label="Select CSV"
        @select="handleFileSelect"
        class="w-full"
      />

      <div v-if="selectedFile" class="text-sm text-gray-700">
        Selected: <span class="font-semibold">{{ selectedFile.name }}</span>
      </div>

      <!-- Results -->
      <div v-if="result" class="flex flex-col gap-3">
        <div class="flex gap-6 text-sm">
          <div class="flex flex-col items-center">
            <span class="text-2xl font-bold text-green-600">{{ result.inserted_count ?? 0 }}</span>
            <span class="text-gray-500 text-xs">Inserted</span>
          </div>
          <div class="flex flex-col items-center">
            <span class="text-2xl font-bold" :class="(result.error_count ?? 0) > 0 ? 'text-red-600' : 'text-gray-400'">
              {{ result.error_count ?? 0 }}
            </span>
            <span class="text-gray-500 text-xs">Errors</span>
          </div>
        </div>

        <DataTable
          v-if="result.errors && result.errors.length > 0"
          :value="result.errors"
          class="text-sm"
          tableStyle="min-width: 100%"
        >
          <Column header="Line" field="line_number" class="w-[80px]" />
          <Column header="Details" field="details" />
        </DataTable>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button
          label="Close"
          severity="secondary"
          @click="handleHide"
        />
        <Button
          label="Upload"
          @click="handleUpload"
          :loading="isUploading"
          :disabled="!selectedFile || isUploading"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
